import React, { useMemo } from 'react';
import { ExamsMetricsUserItem } from './exams-metrics-user-item';
import { useFormik } from 'formik';
import { Filter, FilterContent, FilterSelectNative } from '@/components/custom/filter';
import { useQuery } from 'jsonapi-react';
import { EmptyState } from '@/components/custom/empty-state';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/custom/loader';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import { Table, TableBody, TableHeaderWrapper } from '@/components/ui/table';

export function ExamsMetricsUser({ courses }) {
  const { t } = useTranslation();
  const { data: questionBooksUsers = [], isLoading } = useQuery('question_books_users/reports');

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      status: {
        approved: false,
        disapproved: false,
        review: false
      }
    }
  });

  const getEffectiveStatus = item => {
    const currentTime = Date.now();
    const finishDateStr = item.question_book?.['finished-at'];
    const finishDateMillis = finishDateStr ? new Date(finishDateStr).getTime() : 0;

    // Se o tempo atual for anterior à data de finalização, o status é 'review' (Em análise)
    if (currentTime < finishDateMillis) {
      return 'review';
    }

    return item.question_book?.['question-books-users']?.[0]?.status;
  };

  const filteredQuestionBooksUsers = useMemo(() => {
    const { course, status } = filters.values;

    const selectedStatusKeys = Object.entries(status)
      .filter(([_, isChecked]) => isChecked)
      .map(([key]) => key);

    const allStatusUnchecked = selectedStatusKeys.length === 0;

    return questionBooksUsers.filter(item => {
      // Validação de Filtro de Curso
      const matchesCourse = course === 'blank' || item.question_book?.['course-ids']?.includes(Number(course));

      // Se nada estiver selecionado no status, filtra apenas pelo curso (como na imagem)
      if (allStatusUnchecked) {
        return matchesCourse;
      }

      // Validação de Status Efetivo (tempo real vs banco)
      const effectiveStatus = getEffectiveStatus(item);
      const matchesStatus = selectedStatusKeys.includes(effectiveStatus);

      return matchesStatus && matchesCourse;
    });
  }, [questionBooksUsers, filters.values]);

  const handleFilterCourse = id => {
    filters.setFieldValue('course', id);
  };

  const handleFilterStatus = field => {
    filters.setFieldValue(`status.${field}`, !filters.values.status[field]);
  };

  const statusOptions = [
    {
      label: 'Aprovado',
      value: filters.values.status.approved,
      onChange: () => handleFilterStatus('approved')
    },
    {
      label: 'Reprovado',
      value: filters.values.status.disapproved,
      onChange: () => handleFilterStatus('disapproved')
    },
    {
      label: 'Em análise',
      value: filters.values.status.review,
      onChange: () => handleFilterStatus('review')
    }
  ];

  const TABLE_HEAD = [{ label: t('exams.thExams') }, { label: t('exams.thResults'), width: 132 }, { label: t('exams.thResolutionDate'), width: 160 }, { label: t('exams.thScore'), width: 100 }, { label: '', width: 190 }];

  return (
    <>
      <Filter>
        <FilterContent>
          <FilterSelectNative
            label={t('filter.labelSelectCourse')}
            blankLabel={t('filter.blankLabelAll')}
            value={filters.values.course}
            onChange={e => handleFilterCourse(e.target.value)}
            options={courses}
          />
          <FilterSelectMulti
            label={t('filter.labelSelectStatus')}
            data={statusOptions}
          />
        </FilterContent>
      </Filter>

      {isLoading ? (
        <Loader />
      ) : !isLoading && questionBooksUsers.length > 0 ? (
        <Table className="min-w-[832px] animate-fade-in">
          <TableHeaderWrapper tableHead={TABLE_HEAD} />

          <TableBody>
            {filteredQuestionBooksUsers?.map(item => (
              <ExamsMetricsUserItem
                examStudent={item}
                key={item.id}
              />
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          type="data"
          title="Ainda não temos os resultados."
          text="Quando avaliações forem realizadas os resultados parecerão aqui."
        />
      )}
    </>
  );
}
